import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { type Device } from "../Device";

import { invalidateDeviceListings } from "./invalidateDeviceListings";

interface DeleteDevicesVariables {
  /** Each device is deleted in its own org, read from its `org_id`. */
  devices: readonly Device[];
  /** Move the devices' configurations into the organization's assets instead of deleting them. */
  keepConfigurations: boolean;
}

/**
 * Deletes devices with one request each, since the service has no bulk
 * delete. One failure does not stop the others: the result holds each
 * device's outcome, in the order of `devices`, and the mutation itself never
 * fails, so callers read failures from the result rather than `onError`.
 */
export function useDeleteDevices() {
  const { devices: deviceService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    PromiseSettledResult<void>[],
    Error,
    DeleteDevicesVariables
  >({
    mutationFn: ({ devices, keepConfigurations }) =>
      Promise.allSettled(
        devices.map((device) =>
          deviceService.deleteDevice(device.device_id, {
            resourceOwnerId: device.org_id,
            keepConfigurations,
          }),
        ),
      ),
    onSuccess: (results, { devices }) => {
      invalidateDeviceListings(queryClient, devices);

      // Drop each deleted device's detail record and tokens rather than
      // invalidating them: refetching either would only 404, since deleting a
      // device deletes its tokens too.
      devices.forEach(({ device_id }, index) => {
        if (results[index].status === "fulfilled") {
          queryClient.removeQueries({ queryKey: ["device", device_id] });
          queryClient.removeQueries({ queryKey: ["deviceTokens", device_id] });
        }
      });
    },
  });
}
